import { useState, useEffect, useCallback, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useAuth } from '../../firebase/useAuth';
import { useModal } from '../../context/ModalContext';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useToast } from '../../context/ToastContext';
import { fetchUserWords, fetchUserSentences } from '../../firebase/progress';
import { refetchSingleItem } from '../../firebase/course';
import DetailsModal from '../../components/modals/DetailsModal/DetailsModal';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import MainNavbar from '../../components/MainNavbar';
import MainFooter from '../../components/MainFooter';
import Spinner from '../../components/common/Spinner';
import ProgressSidebarControls from '../../components/progress/ProgressSidebarControls';
import ProgressHeader from '../../components/progress/ProgressHeader';
import MobileSidebar from '../../components/course/MobileSidebar';

import { FunnelIcon, ChevronDownIcon, BookmarkSquareIcon } from '@heroicons/react/24/solid';
import ItemGrid from '../../components/common/ItemGrid';
import CourseContentGridSkeleton from '../../components/course/skeletons/CourseContentGridSkeleton';

function ProgressContentPage({ contentType = 'words' }) {
  const { userId } = useAuth();
  const { showModal, hideModal } = useModal();
  const { learnedWordIds, learnedSentenceIds } = useMetadata();
  const { showToast } = useToast();

  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const lastVisibleRef = useRef(null);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedSrsStage, setSelectedSrsStage] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('level');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const itemsPerPage = contentType === 'words' ? 54 : 36;
  const [isMobileFiltersVisible, setIsMobileFiltersVisible] = useState(false);
  const [showStickyFilterButton, setShowStickyFilterButton] = useState(false);
  const mobileFiltersRef = useRef(null);

  // Stats are now loaded from global context in ProgressHeader

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyFilterButton(!entry.isIntersecting);
      },
      { rootMargin: '-80px 0px 0px 0px' }
    );
    const currentRef = mobileFiltersRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  // Handle item updates from DetailsModal actions
  const handleItemUpdate = useCallback(async (itemId) => {
    try {
      // Refetch the updated item data from server
      const updatedItem = await refetchSingleItem(itemId, userId, contentType);
      
      setItems(prevItems => {
        const itemIndex = prevItems.findIndex(item => item.id === itemId);
        if (itemIndex === -1) return prevItems; // Item no longer in array
        
        const newItems = [...prevItems];
        // Replace with the updated item data
        newItems[itemIndex] = updatedItem;
        return newItems;
      });
    } catch (error) {
      console.error('Failed to refetch item:', error);
    }
  }, [userId, contentType]);

  const loadItems = useCallback(
    async (isInitialLoad = false) => {
      if (!userId) return;
      setIsLoading(true);
      const fetchFunction = contentType === 'words' ? fetchUserWords : fetchUserSentences;
      try {
        const lastDoc = isInitialLoad ? null : lastVisibleRef.current;
        const result = await fetchFunction(userId, {
          level: selectedLevel,
          srsStage: selectedSrsStage,
          status: selectedStatus,
          sortBy,
          limitValue: itemsPerPage,
          lastVisible: lastDoc,
        });
        const fetchedItems = contentType === 'words' ? result.words : result.sentences;
        setItems((prev) => (isInitialLoad ? fetchedItems : [...prev, ...fetchedItems]));
        lastVisibleRef.current = result.lastVisible;
        setHasMore(result.hasMore);
      } catch (error) {
        console.error(`Failed to load ${contentType}:`, error);
        showToast(`Failed to load ${contentType}.`, 'error');
      } finally {
        setIsLoading(false);
      }
    },
    // showToast is stable from context but eslint can't detect it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, contentType, selectedLevel, selectedSrsStage, selectedStatus, sortBy, itemsPerPage]
  );

  useEffect(() => {
    setItems([]);
    lastVisibleRef.current = null;
    setHasMore(true);
    if (userId) loadItems(true);
  }, [userId, contentType, selectedLevel, selectedSrsStage, selectedStatus, sortBy, loadItems]);

  const handleOpenModal = useCallback(
    (item) => {
      showModal(<DetailsModal item={item} handleClose={hideModal} onUpdate={handleItemUpdate} />);
    },
    [showModal, hideModal, handleItemUpdate]
  );

  const passedFilterProps = {
    level: selectedLevel,
    setLevel: setSelectedLevel,
    srsStage: selectedSrsStage,
    setSrsStage: setSelectedSrsStage,
    status: selectedStatus,
    setStatus: setSelectedStatus,
    sortBy,
    setSortBy,
    contentType,
  };

  return (
    <SkeletonTheme baseColor="var(--color-skeleton-base)" highlightColor="var(--color-skeleton-highlight)">
      <div className="flex flex-col min-h-screen bg-background-primary">
        <MainNavbar />
        <main className="flex-grow">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <ProgressHeader contentType={contentType} />
            <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-6 gap-8">
              <aside className="hidden lg:block lg:col-span-1 xl:col-span-1 lg:sticky top-24 self-start">
                <ProgressSidebarControls {...passedFilterProps} />
              </aside>
              <div className="lg:col-span-4 xl:col-span-5">
                <div ref={mobileFiltersRef} className="lg:hidden mb-6">
                  <button
                    onClick={() => setIsMobileFiltersVisible(!isMobileFiltersVisible)}
                    className="flex w-full items-center justify-between rounded-lg bg-background-secondary p-3 shadow-sm border border-border hover:bg-background-secondary/80 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <FunnelIcon className="h-5 w-5 text-content-secondary" />
                      <span className="font-semibold text-content-primary">Filters</span>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-content-secondary transition-transform duration-200 ${isMobileFiltersVisible ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isMobileFiltersVisible && (
                    <div className="mt-4 rounded-lg bg-background-secondary p-4 border border-border animate-fade-in">
                      <ProgressSidebarControls {...passedFilterProps} isMobileView />
                    </div>
                  )}
                </div>

                {isLoading && items.length === 0 ? (
                  <CourseContentGridSkeleton contentType={contentType} />
                ) : !isLoading && items.length === 0 ? (
                  <div className="text-center py-16 flex flex-col items-center">
                    <BookmarkSquareIcon className="w-20 h-20 text-content-secondary mb-6" />
                    <h3 className="text-xl font-semibold text-content-primary">Your Progress Page</h3>
                    <p className="text-content-secondary mt-2">{`Your learned ${contentType} will appear here as you study.`}</p>
                  </div>
                ) : (
                  <InfiniteScroll
                    dataLength={items.length}
                    next={() => loadItems(false)}
                    hasMore={hasMore}
                    loader={
                      <div className="my-8">
                        <Spinner />
                      </div>
                    }
                    endMessage={<p className="text-center text-content-secondary my-8">You&apos;ve reached the end!</p>}
                  >
                    <ItemGrid items={items} contentType={contentType} onCardClick={handleOpenModal} learnedIds={contentType === 'words' ? learnedWordIds : learnedSentenceIds} />
                  </InfiniteScroll>
                )}
              </div>
            </div>
          </div>
        </main>
        <MainFooter />
        <>
          {showStickyFilterButton && (
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden fixed top-24 left-4 z-30 flex items-center justify-center h-12 w-12 bg-background-primary rounded-full shadow-lg border border-border hover:shadow-xl transition-all duration-200"
              aria-label="Open Filters"
            >
              <FunnelIcon className="h-6 w-6 text-content-primary" />
            </button>
          )}
          <MobileSidebar isOpen={isMobileSidebarOpen} onClose={() => setIsMobileSidebarOpen(false)}>
            <ProgressSidebarControls {...passedFilterProps} />
          </MobileSidebar>
        </>
      </div>
    </SkeletonTheme>
  );
}

export default ProgressContentPage;
